import React, { useCallback, useEffect, useRef, useState } from "react";
import { getUserKpis, GetUserKpisParams, UserKpiResponse } from "./UserHomeApi";
import { UserHomeDashboard } from "./UserHomeDashboard";
import { UserHomeForm } from "./UserHomeForm";

/**
 * Wrapper + basic routing (single screen module for dashboard)
 * You can integrate into your app router as <UserHomeIndex />
 */
export const UserHomeIndex: React.FC = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const tenantId = user?.tenantId || '';
  const tenantName = user?.tenantName || '';
  const userId = user?.id || '';
  const [data, setData] = useState<UserKpiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const controller = useRef<AbortController | null>(null);

  const load = useCallback(async (p: Omit<GetUserKpisParams, "signal">) => {
    try {
      setError(null);
      setLoading(true);
      controller.current?.abort();
      controller.current = new AbortController();
      const res = await getUserKpis({ ...p, signal: controller.current.signal });
      setData(res);
    } catch (e: any) {
      setError(e?.message || "Failed to load KPIs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    return () => controller.current?.abort();
  }, []);

  return (
    <div className="p-4 space-y-4">
      <UserHomeForm
        mode="view"
        initialTenantId={tenantId}
        initialUserId={userId}
        onApply={load}
        screenTitle="User Home Dashboard"
      />

      <UserHomeDashboard
        generalTasks={data?.general_tasks ?? []}
        projectTasks={data?.project_tasks ?? []}
        overdueProject={data?.overdue_tasks ?? []}
        overdueAll={data?.overdue_tasks_assignee_all_entities ?? []}
        completionRate={data?.completion_rate_by_role ?? []}
        countsByEntityStatus={data?.counts_by_entity_status ?? []}
        activity={data?.recent_assignments ?? []}
        topPriorities={data?.my_top_priorities_next_7_days ?? []}
        pendingAssignedByMe={data?.pending_assigned_by_me ?? { count: 0 }}
        loading={loading}
        error={error}
        onRetry={() => load({ tenantId, userId })}
      />
    </div>
  );
};

export default UserHomeIndex;